import React, { useState, useEffect, useRef, useContext } from 'react';
import axios from 'axios';
import { FaBuilding, FaChevronDown } from 'react-icons/fa';
import OrganizationContext from '../../context/OrganizationContext';
import AuthContext from '../../context/AuthContext';

const CompactOrgSelector = ({ onSelectOrganization }) => {
  const { user } = useContext(AuthContext);
  const { currentOrganization, updateCurrentOrganization } = useContext(OrganizationContext);
  const [organizations, setOrganizations] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);

  // Fetch organizations on component mount
  useEffect(() => {
    fetchOrganizations();
    
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      if (user?.isMultiOrg) {
        // For multi-org users, fetch all their organizations
        try {
          const res = await axios.get('/api/organizations/user');
          setOrganizations(res.data);
        } catch (fetchErr) {
          console.error('Error fetching organizations for multi-org user:', fetchErr);
          // Create a minimal org object if API fails
          if (user.organization) {
            setOrganizations([{
              _id: typeof user.organization === 'object' ? user.organization._id : user.organization,
              name: user.organizationName || (user.organization && typeof user.organization === 'object' ? user.organization.name : '') || user.name || user.company || 'Organization',
              industry: user.industryType || 'Other',
              createdAt: new Date().toISOString()
            }]);
          }
        }
      } else if (user) {
        // For single-org users, create an array with just their organization
        try {
          const res = await axios.get('/api/organizations/current');
          if (res.data) {
            setOrganizations([res.data]);
          } else {
            // Fallback if API returns empty data
            setOrganizations([{
              _id: typeof user.organization === 'object' ? user.organization._id : (user.organization || 'default-org-id'),
              name: user.organizationName || (user.organization && typeof user.organization === 'object' ? user.organization.name : '') || user.name || user.company || 'Organization',
              industry: user.industryType || 'Other',
              createdAt: new Date().toISOString()
            }]);
          }
        } catch (fetchErr) {
          console.error('Error fetching current organization:', fetchErr);
          setOrganizations([{
            _id: typeof user.organization === 'object' ? user.organization._id : (user.organization || 'default-org-id'),
            name: user.organizationName || (user.organization && typeof user.organization === 'object' ? user.organization.name : '') || user.name || user.company || 'Organization',
            industry: user.industryType || 'Other',
            createdAt: new Date().toISOString()
          }]);
        }
      }
      setLoading(false);
    } catch (err) {
      console.error('Error in fetchOrganizations:', err);
      setLoading(false);
    }
  };

  const handleSelectOrganization = (org) => {
    if (onSelectOrganization) {
      onSelectOrganization(org);
    }
    
    // Update the organization context
    if (updateCurrentOrganization) {
      updateCurrentOrganization(org);
    }
    
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Get current organization name
  const currentOrgName = currentOrganization?.name || 
    organizations.find(org => org._id === user?.organization)?._id || 
    user?.organizationName || 
    'Select Organization';

  const styles = {
    container: {
      position: 'relative',
      marginRight: '15px',
      zIndex: 100
    },
    selector: {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: '#FFFAFA',
      color: '#243444',
      border: '1px solid #D2BA7B',
      borderRadius: '4px',
      padding: '6px 12px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 'bold',
      minWidth: '250px',
      justifyContent: 'space-between'
    },
    icon: {
      marginRight: '8px',
      fontSize: '16px'
    },
    chevron: {
      marginLeft: '8px',
      fontSize: '12px',
      transition: 'transform 0.3s ease'
    },
    chevronOpen: {
      transform: 'rotate(180deg)'
    },
    dropdown: {
      position: 'absolute',
      top: '100%',
      left: '0',
      backgroundColor: '#FFFAFA',
      border: '1px solid #D2BA7B',
      borderRadius: '4px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      width: '300px',
      maxHeight: '300px',
      overflowY: 'auto',
      zIndex: 1001,
      display: isOpen ? 'block' : 'none'
    },
    orgItem: {
      padding: '10px 15px',
      borderBottom: '1px solid #eee',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      display: 'flex',
      flexDirection: 'column'
    },
    orgName: {
      fontWeight: 'bold',
      color: '#243444',
      fontSize: '14px'
    },
    orgIndustry: {
      color: '#4C5454',
      fontSize: '12px',
      marginTop: '3px'
    },
    activeOrg: {
      backgroundColor: '#f0f0f0'
    },
    loading: {
      padding: '10px 15px',
      color: '#4C5454',
      textAlign: 'center'
    }
  };

  return (
    <div style={styles.container} ref={dropdownRef} data-testid="organization-selector">
      <div 
        style={styles.selector} 
        onClick={toggleDropdown}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FaBuilding style={styles.icon} />
          <span style={{ maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {currentOrganization?.name || (organizations[0]?.name || 'Organization')}
          </span>
        </div>
        <FaChevronDown style={{
          ...styles.chevron,
          ...(isOpen ? styles.chevronOpen : {})
        }} />
      </div>
      
      <div style={styles.dropdown}>
        {loading ? (
          <div style={styles.loading}>Loading organizations...</div>
        ) : (
          organizations.map(org => (
            <div
              key={org._id}
              style={{
                ...styles.orgItem,
                ...(currentOrganization?._id === org._id ? styles.activeOrg : {})
              }}
              onClick={() => handleSelectOrganization(org)}
            >
              <div style={styles.orgName}>{org.name}</div>
              <div style={styles.orgIndustry}>{org.industry}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CompactOrgSelector;
